import { useMemo, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import { BarChart3, CheckCircle2, Clock, X, Loader2 } from "lucide-react";

import { Advisor, useGetAdvisors } from "@/api/advisor/useGetAdvisors";
import { useGetCapguardPollData } from "@/api/poll/useGetCapguardPollData";
import { useUpdatePollData } from "@/api/poll/useUpdatePollData";
import { Button } from "@/components/Button/Button";
import { cn } from "@/lib/utils";
import { useKeycloak } from "@/providers/KeycloakProvider";

const POLL_SECURITY_CODE = "CAPGUARDPOLL";
const POLL_FIGURE_CODE = "capguardPoll";

interface ParsedPoll {
  /** Stable id derived from title + deadline ms */
  id: string;
  title: string;
  description: string;
  options: string[];
  /** index of selected option, keyed by contactId */
  votes: Record<string, number>;
  deadline: Date | null;
  isClosed: boolean;
  /** Raw date components [year, month, day] (month 1-indexed) used to address the observation. */
  rawDate: number[];
  /** Original "|"-separated parts of stringValue (used when rebuilding the value). */
  rawParts: string[];
}

/**
 * Parses a poll Figure into a structured ParsedPoll.
 *
 * Expected stringValue format (pipe-separated):
 *   "Title|Description|opt1;opt2;opt3|contactId~voteIdx;contactId~voteIdx"
 *
 * Note: contactId and voteIdx are separated by "~" because "=", "#"
 * and ":" are reserved by the outer keyFigures string format.
 *
 * date is [year, month, day] where month is 1-indexed.
 */
function parsePoll(figure: {
  date: number[];
  stringValue: string;
}): ParsedPoll | null {
  if (!figure?.stringValue) return null;
  const parts = figure.stringValue.split("|");
  const title = (parts[0] ?? "").trim();
  const description = (parts[1] ?? "").trim();
  const options =
    parts[2]
      ?.split(";")
      .map((s) => s.trim())
      .filter(Boolean) ?? [];
  const votes: Record<string, number> = {};
  if (parts[3]) {
    for (const pair of parts[3].split(";")) {
      // contactId~voteIdx — "~" replaces the old "=" separator since "=",
      // "#" and ":" are reserved by the outer keyFigures format.
      const [cId, vIdx] = pair.split("~");
      if (cId && vIdx !== undefined) {
        const idx = Number.parseInt(vIdx, 10);
        if (!Number.isNaN(idx)) votes[cId.trim()] = idx;
      }
    }
  }

  let deadline: Date | null = null;
  if (Array.isArray(figure.date) && figure.date.length >= 3) {
    const [y, m, d] = figure.date;
    deadline = new Date(y, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 999);
  }
  const isClosed = deadline ? deadline.getTime() < Date.now() : false;

  return {
    id: `${title}-${deadline?.getTime() ?? "no-date"}`,
    title,
    description,
    options,
    votes,
    deadline,
    isClosed,
    rawDate: figure.date,
    rawParts: parts,
  };
}

function formatDeadline(d: Date | null): string {
  if (!d) return "";
  return d.toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Format [yyyy, m, d] as yyyy-MM-dd (zero-padded). */
function formatObservationDate(rawDate: number[]): string {
  const [y, m, d] = rawDate;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Build the updated stringValue with the current user's vote applied.
 * Preserves title / description / options exactly as they were stored.
 */
function buildUpdatedStringValue(
  poll: ParsedPoll,
  contactId: string,
  optionIdx: number
): string {
  const nextVotes: Record<string, number> = {
    ...poll.votes,
    [contactId]: optionIdx,
  };
  const votesStr = Object.entries(nextVotes)
    .map(([cId, idx]) => `${cId}~${idx}`)
    .join(";");
  const [title = "", description = "", opts = ""] = poll.rawParts;
  return `${title}|${description}|${opts}|${votesStr}`;
}

/**
 * Build the updated stringValue with the current user's vote removed.
 */
function buildRemovedVoteStringValue(
  poll: ParsedPoll,
  contactId: string
): string {
  const nextVotes = Object.fromEntries(
    Object.entries(poll.votes).filter(([cId]) => cId !== contactId)
  );
  const votesStr = Object.entries(nextVotes)
    .map(([cId, idx]) => `${cId}~${idx}`)
    .join(";");
  const [title = "", description = "", opts = ""] = poll.rawParts;
  return `${title}|${description}|${opts}|${votesStr}`;
}

/**
 * Build the keyFigures mutation string.
 *
 *   ***:capguardPoll=YYYY-MM-DD=<newStringValue>
 *
 * The leading "***:" tells the backend to merge with existing observations
 * (so other polls under the same code are preserved).
 */
function buildKeyFiguresString(
  poll: ParsedPoll,
  newStringValue: string
): string {
  const date = formatObservationDate(poll.rawDate);
  return `***:${POLL_FIGURE_CODE}=${date}=${newStringValue}`;
}

/** Shared colour tokens per option index (wraps at 6). Used in both panels. */
const OPTION_COLORS = [
  {
    dot: "bg-blue-500",
    border: "border-blue-500",
    barActive: "bg-blue-100 dark:bg-blue-900/40",
    barPassive: "bg-blue-50 dark:bg-blue-950/20",
    check: "text-blue-600 dark:text-blue-400",
    label: "text-blue-700 dark:text-blue-300",
    hover: "hover:border-blue-300 dark:hover:border-blue-700",
    heading: "text-blue-700 dark:text-blue-300",
  },
  {
    dot: "bg-emerald-500",
    border: "border-emerald-500",
    barActive: "bg-emerald-100 dark:bg-emerald-900/40",
    barPassive: "bg-emerald-50 dark:bg-emerald-950/20",
    check: "text-emerald-600 dark:text-emerald-400",
    label: "text-emerald-700 dark:text-emerald-300",
    hover: "hover:border-emerald-300 dark:hover:border-emerald-700",
    heading: "text-emerald-700 dark:text-emerald-300",
  },
  {
    dot: "bg-amber-500",
    border: "border-amber-500",
    barActive: "bg-amber-100 dark:bg-amber-900/40",
    barPassive: "bg-amber-50 dark:bg-amber-950/20",
    check: "text-amber-600 dark:text-amber-400",
    label: "text-amber-700 dark:text-amber-300",
    hover: "hover:border-amber-300 dark:hover:border-amber-700",
    heading: "text-amber-700 dark:text-amber-300",
  },
  {
    dot: "bg-rose-500",
    border: "border-rose-500",
    barActive: "bg-rose-100 dark:bg-rose-900/40",
    barPassive: "bg-rose-50 dark:bg-rose-950/20",
    check: "text-rose-600 dark:text-rose-400",
    label: "text-rose-700 dark:text-rose-300",
    hover: "hover:border-rose-300 dark:hover:border-rose-700",
    heading: "text-rose-700 dark:text-rose-300",
  },
  {
    dot: "bg-purple-500",
    border: "border-purple-500",
    barActive: "bg-purple-100 dark:bg-purple-900/40",
    barPassive: "bg-purple-50 dark:bg-purple-950/20",
    check: "text-purple-600 dark:text-purple-400",
    label: "text-purple-700 dark:text-purple-300",
    hover: "hover:border-purple-300 dark:hover:border-purple-700",
    heading: "text-purple-700 dark:text-purple-300",
  },
  {
    dot: "bg-cyan-500",
    border: "border-cyan-500",
    barActive: "bg-cyan-100 dark:bg-cyan-900/40",
    barPassive: "bg-cyan-50 dark:bg-cyan-950/20",
    check: "text-cyan-600 dark:text-cyan-400",
    label: "text-cyan-700 dark:text-cyan-300",
    hover: "hover:border-cyan-300 dark:hover:border-cyan-700",
    heading: "text-cyan-700 dark:text-cyan-300",
  },
] as const;

function PollDialog({
  poll,
  open,
  onClose,
  currentContactId,
  submitting,
  onVote,
  onRemoveVote,
  advisors,
}: {
  poll: ParsedPoll;
  open: boolean;
  onClose: () => void;
  currentContactId: string | null;
  submitting: boolean;
  onVote: (poll: ParsedPoll, optionIdx: number) => void;
  onRemoveVote: (poll: ParsedPoll) => void;
  advisors: Advisor[];
}) {
  const totalVotes = Object.keys(poll.votes).length;
  const counts = useMemo(() => {
    const c = new Array(poll.options.length).fill(0) as number[];
    for (const idx of Object.values(poll.votes)) {
      if (idx >= 0 && idx < c.length) c[idx] += 1;
    }
    return c;
  }, [poll.votes, poll.options.length]);

  const advisorById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const a of advisors) map[String(a.id)] = a.name;
    return map;
  }, [advisors]);

  /** Names of voters who voted for a given option index. */
  const votersByOption = useMemo(() => {
    const groups: string[][] = poll.options.map(() => []);
    for (const [cId, idx] of Object.entries(poll.votes)) {
      if (idx >= 0 && idx < groups.length) {
        groups[idx].push(advisorById[cId] ?? cId);
      }
    }
    return groups;
  }, [poll.votes, poll.options, advisorById]);

  const myVote =
    currentContactId && currentContactId in poll.votes
      ? poll.votes[currentContactId]
      : null;
  const canVote = !poll.isClosed && !!currentContactId && !submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <div className="relative flex items-start gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
            <BarChart3 className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight">
              {poll.title || "Poll"}
            </h3>
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {poll.deadline && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Frist {formatDeadline(poll.deadline)}
                </span>
              )}
              <span
                className={cn(
                  "inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold",
                  poll.isClosed
                    ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                    : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                )}
              >
                {poll.isClosed ? "Closed" : "Open"}
              </span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span>
                {totalVotes} stemme{totalVotes === 1 ? "" : "r"}
              </span>
            </div>
          </div>
          <Button size="sm" variant="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row">
          {/* Left: poll options */}
          <div className="flex-1 min-w-0 px-5 py-4 space-y-3">
            {poll.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                {poll.description}
              </p>
            )}

            <ul className="space-y-2">
              {poll.options.map((opt, idx) => {
                const c = counts[idx] ?? 0;
                const pct = totalVotes > 0 ? (c / totalVotes) * 100 : 0;
                const isMyVote = myVote === idx;
                const col = OPTION_COLORS[idx % OPTION_COLORS.length];
                return (
                  <li key={`${opt}-${idx}`}>
                    <button
                      type="button"
                      disabled={!canVote}
                      onClick={() => canVote && onVote(poll, idx)}
                      className={cn(
                        "relative w-full overflow-hidden rounded-xl border-2 text-left transition-all duration-200 bg-white dark:bg-gray-900",
                        isMyVote
                          ? cn(col.border, "shadow-sm")
                          : "border-gray-200 dark:border-gray-700",
                        canVote &&
                          !isMyVote &&
                          cn(col.hover, "hover:shadow-sm"),
                        !canVote && "cursor-default"
                      )}
                    >
                      <div
                        className={cn(
                          "absolute inset-y-0 left-0 transition-[width] duration-700",
                          isMyVote ? col.barActive : col.barPassive
                        )}
                        style={{ width: `${pct}%` }}
                      />
                      <div className="relative flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                        <span className="flex items-center gap-2 min-w-0">
                          {isMyVote ? (
                            <CheckCircle2
                              className={cn("h-4 w-4 shrink-0", col.check)}
                            />
                          ) : (
                            <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                          )}
                          <span
                            className={cn(
                              "font-medium truncate",
                              isMyVote
                                ? col.label
                                : "text-gray-800 dark:text-gray-100"
                            )}
                          >
                            {opt}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "shrink-0 text-xs tabular-nums font-semibold whitespace-nowrap",
                            isMyVote
                              ? col.check
                              : "text-gray-400 dark:text-gray-500"
                          )}
                        >
                          {c > 0 && (
                            <span className="mr-1 opacity-70">{c} ·</span>
                          )}
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Footer hint */}
            <div className="flex items-center justify-between gap-2 text-xs text-gray-400 dark:text-gray-500 pt-1">
              <span>
                {poll.isClosed
                  ? "Avstemningen er avsluttet."
                  : !currentContactId
                  ? "Logg inn med tilknyttet kontakt for å stemme."
                  : myVote !== null
                  ? "Klikk et alternativ for å endre stemmen din."
                  : "Klikk et alternativ for å stemme."}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                {myVote !== null && !poll.isClosed && !submitting && (
                  <button
                    type="button"
                    onClick={() => onRemoveVote(poll)}
                    className="text-xs text-gray-400 hover:text-rose-500 dark:hover:text-rose-400 underline underline-offset-2 transition-colors"
                  >
                    Fjern stemme
                  </button>
                )}
                {submitting && (
                  <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Lagrer…
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Right: voter list grouped by option */}
          <div className="w-full sm:w-64 shrink-0 border-t sm:border-t-0 sm:border-l border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40 px-4 py-4">
            <p className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">
              Hvem har stemt ({totalVotes})
            </p>
            {totalVotes === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Ingen stemmer ennå.
              </p>
            ) : (
              <div className="space-y-4">
                {poll.options.map((opt, idx) => {
                  const voters = votersByOption[idx];
                  if (!voters || voters.length === 0) return null;
                  const col = OPTION_COLORS[idx % OPTION_COLORS.length];
                  return (
                    <div key={`group-${idx}`}>
                      {/* Group header */}
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span
                          className={cn(
                            "h-2 w-2 shrink-0 rounded-full",
                            col.dot
                          )}
                        />
                        <span
                          className={cn(
                            "text-[11px] font-semibold truncate",
                            col.heading
                          )}
                        >
                          {opt}
                        </span>
                        <span className="ml-auto text-[10px] font-bold text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
                          {voters.length}
                        </span>
                      </div>
                      {/* Voter names */}
                      <ul className="space-y-1 pl-3.5">
                        {voters.map((name) => (
                          <li
                            key={`${idx}-${name}`}
                            className="text-xs text-gray-600 dark:text-gray-400 truncate"
                          >
                            {name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PollButton({
  poll,
  currentContactId,
  onClick,
}: {
  poll: ParsedPoll;
  currentContactId: string | null;
  onClick: () => void;
}) {
  const totalVotes = Object.keys(poll.votes).length;
  const myVoteIdx =
    currentContactId && currentContactId in poll.votes
      ? poll.votes[currentContactId]
      : null;
  const hasVoted = myVoteIdx !== null;
  const myVoteLabel = hasVoted ? poll.options[myVoteIdx!] ?? null : null;
  const needsVote = !poll.isClosed && !hasVoted;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-colors",
        needsVote
          ? "border-indigo-400 dark:border-indigo-500 bg-white dark:bg-gray-900 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 shadow-sm"
          : hasVoted
          ? "border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30"
          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
      )}
      title={poll.title}
    >
      {hasVoted ? (
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-blue-500 dark:text-blue-400" />
      ) : needsVote ? (
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 dark:bg-indigo-500 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-500 dark:bg-indigo-400" />
        </span>
      ) : (
        <BarChart3 className="h-3.5 w-3.5 shrink-0" />
      )}
      <span className="max-w-[140px] truncate">{poll.title || "Poll"}</span>
      {hasVoted && myVoteLabel ? (
        <span className="text-blue-400 dark:text-blue-500 truncate max-w-[80px] opacity-80">
          · {myVoteLabel}
        </span>
      ) : (
        <span className="text-gray-400 dark:text-gray-500 tabular-nums">
          · {totalVotes}
        </span>
      )}
    </button>
  );
}

export const CapguardPoll = () => {
  const { data, refetch } = useGetCapguardPollData();
  const { updatePollData, submitting } = useUpdatePollData();
  const { linkedContact } = useKeycloak();
  const { data: advisorsData } = useGetAdvisors();
  const advisors = advisorsData?.contacts ?? [];

  const [openId, setOpenId] = useState<string | null>(null);

  const polls = useMemo<ParsedPoll[]>(() => {
    const figures = data?.security?.figuresAsObject?.figures?.capguardPoll;
    if (!Array.isArray(figures)) return [];
    return figures
      .map(parsePoll)
      .filter((p): p is ParsedPoll => p !== null && p.options.length > 0);
  }, [data]);

  /** Fetches fresh poll data and returns the up-to-date version of a given poll (matched by id). */
  const refetchAndFindPoll = async (poll: ParsedPoll): Promise<ParsedPoll> => {
    const { data: fresh } = await refetch();
    const figures = fresh?.security?.figuresAsObject?.figures?.capguardPoll;
    const freshPoll = Array.isArray(figures)
      ? figures
          .map(parsePoll)
          .find((p): p is ParsedPoll => p !== null && p.id === poll.id)
      : undefined;
    return freshPoll ?? poll;
  };

  const handleVote = async (poll: ParsedPoll, optionIdx: number) => {
    if (!linkedContact || poll.isClosed) return;
    const latestPoll = await refetchAndFindPoll(poll);
    if (latestPoll.isClosed) return; // deadline may have passed since dialog opened
    const newStringValue = buildUpdatedStringValue(
      latestPoll,
      String(linkedContact),
      optionIdx
    );
    const keyFigures = buildKeyFiguresString(latestPoll, newStringValue);
    const res = await updatePollData(POLL_SECURITY_CODE, keyFigures);
    if (res) await refetch();
  };

  const handleRemoveVote = async (poll: ParsedPoll) => {
    if (!linkedContact || poll.isClosed) return;
    const latestPoll = await refetchAndFindPoll(poll);
    if (latestPoll.isClosed) return; // deadline may have passed since dialog opened
    const newStringValue = buildRemovedVoteStringValue(
      latestPoll,
      String(linkedContact)
    );
    const keyFigures = buildKeyFiguresString(latestPoll, newStringValue);
    const res = await updatePollData(POLL_SECURITY_CODE, keyFigures);
    if (res) await refetch();
  };

  if (polls.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {polls.map((poll) => (
        <PollButton
          key={poll.id}
          poll={poll}
          currentContactId={linkedContact ? String(linkedContact) : null}
          onClick={() => {
            setOpenId(poll.id);
            refetch();
          }}
        />
      ))}
      {polls.map((poll) => (
        <PollDialog
          key={`dlg-${poll.id}`}
          poll={poll}
          open={openId === poll.id}
          onClose={() => setOpenId(null)}
          currentContactId={linkedContact ? String(linkedContact) : null}
          submitting={submitting}
          onVote={handleVote}
          onRemoveVote={handleRemoveVote}
          advisors={advisors}
        />
      ))}
    </div>
  );
};
